export type DateParts = { date: string; time: string } | null;

// Used to format keys from backend response for display
export const formatStringForDisplay = (input: string) => {
  // Replace underscores with spaces
  const spacedString = input.replaceAll('_', ' ');

  // Convert camelCase to spaces and normalize
  const normalizedString = spacedString.replaceAll(/([a-z])([A-Z])/g, '$1 $2');

  // Capitalize each word
  return normalizedString
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Used to format column_names for filtering in request parameters "filter.columnName"
export const snakeToCamel = (str: string) => {
  return (
    // replace underscore + letter with letter in uppercase
    str.replaceAll(/_([a-zA-Z0-9])/g, (_, letter: string) => letter.toUpperCase())
  );
};

// Implementation of a range function similar to Python's range
export const getRange = (start: number, stop?: number, step = 1): number[] => {
  if (stop === undefined) {
    stop = start;
    start = 0;
  }

  if (step === 0) {
    throw new Error('getRange(): step cannot be zero.');
  }

  const result: number[] = [];
  const increasing = step > 0;

  if (increasing) {
    for (let i = start; i < stop; i += step) {
      result.push(i);
    }
  } else {
    for (let i = start; i > stop; i += step) {
      result.push(i);
    }
  }

  return result;
};

export const extractNameFromEmail = (email: string) => {
  if (!email) return '';

  const [localPart] = email.split('@');

  const nameParts = localPart?.split('.');

  return nameParts?.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
};

export const getFormattedDateParts = (dateString: string, hour12: boolean): DateParts => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  return {
    date: date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' }),
    time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12 }),
  };
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return '';

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatCompactNumber = (num: number | undefined | null): string => {
  if (num === undefined || num === null) return '--';

  const absoluteValue = Math.abs(num);

  const getCompactString = (value: number, suffix: 'K' | 'M' | 'B') => {
    const decimals = Math.abs(value) >= 100 ? 0 : 1;
    const rounded = value.toFixed(decimals).replace(/\.0$/, '');
    return `${rounded}${suffix}`;
  };

  if (absoluteValue >= 1_000_000_000) {
    return getCompactString(num / 1_000_000_000, 'B');
  }

  if (absoluteValue >= 1_000_000) {
    return getCompactString(num / 1_000_000, 'M');
  }

  if (absoluteValue >= 1_000) {
    return getCompactString(num / 1_000, 'K');
  }

  return formatNumber(num);
};

const formatter = new Intl.NumberFormat('en-US');
export const formatNumber = (value: number) => formatter.format(value);

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength).trimEnd()}…`;
};
